import {
  Children,
  isValidElement,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import './CodeBlock.css';

// Pretty-print language labels. Unknown languages fall back to UPPERCASE
// (and an empty fence shows as `CODE`).
const LANG_LABELS: Record<string, string> = {
  bash: 'Bash',
  sh: 'Shell',
  shell: 'Shell',
  zsh: 'Shell',
  sql: 'SQL',
  js: 'JavaScript',
  javascript: 'JavaScript',
  jsx: 'JSX',
  ts: 'TypeScript',
  typescript: 'TypeScript',
  tsx: 'TSX',
  json: 'JSON',
  yaml: 'YAML',
  yml: 'YAML',
  toml: 'TOML',
  text: 'Text',
  txt: 'Text',
  python: 'Python',
  py: 'Python',
  ruby: 'Ruby',
  rb: 'Ruby',
  go: 'Go',
  rust: 'Rust',
  rs: 'Rust',
  hcl: 'HCL',
  tf: 'HCL',
  terraform: 'HCL',
  dockerfile: 'Dockerfile',
  docker: 'Dockerfile',
  markdown: 'Markdown',
  md: 'Markdown',
  html: 'HTML',
  css: 'CSS',
  scss: 'SCSS',
  diff: 'Diff',
};

type Props = {
  lang?: string;
  code: string;
};

export function CodeBlock({ lang, code }: Props) {
  const [copied, setCopied] = useState(false);
  const label = lang
    ? (LANG_LABELS[lang.toLowerCase()] ?? lang.toUpperCase())
    : 'Code';

  function handleCopy() {
    if (!navigator.clipboard) return;
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        /* clipboard unavailable — silently no-op */
      });
  }

  return (
    <div className="code-block" data-lang={lang || undefined}>
      <div className="code-block-header">
        <span className="code-block-lang">{label}</span>
        <button
          type="button"
          className={`code-block-copy${copied ? ' is-copied' : ''}`}
          onClick={handleCopy}
          aria-label={copied ? 'Copied' : 'Copy code to clipboard'}
        >
          {copied ? (
            <>
              <i className="fa-solid fa-check" aria-hidden="true"></i>
              Copied
            </>
          ) : (
            <>
              <i className="fa-regular fa-copy" aria-hidden="true"></i>
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="code-block-pre">
        <code>{code}</code>
      </pre>
    </div>
  );
}

// Adapter for react-markdown's `components.pre` prop. Pulls the language
// hint from the inner <code className="language-X"> and the text content
// from its children, then delegates to <CodeBlock>. Falls back to a plain
// <pre> if the shape is unexpected (e.g., raw HTML <pre> with no <code>).
export function MarkdownPre({ children }: { children?: ReactNode }) {
  const first = Children.toArray(children)[0];
  if (
    !isValidElement<{ className?: string; children?: ReactNode }>(first) ||
    (first as ReactElement).type !== 'code'
  ) {
    return <pre>{children}</pre>;
  }
  const className = first.props.className ?? '';
  const lang = className.match(/language-(\S+)/)?.[1];
  const code = extractText(first.props.children).replace(/\n$/, '');
  return <CodeBlock lang={lang} code={code} />;
}

function extractText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return extractText(node.props.children);
  }
  return '';
}
